import React from "react";
import { Input, InputNumber, Select, DatePicker } from "antd";
import moment from "moment";
import {
  ATTR_TYPE,
  BOOL_TRUE,
  BOOL_FALSE,
  isBlankValue,
  isTruthyBoolString,
  normalizeAttrType,
  numberToWire,
  dateToWire,
  stripZoneRegion,
} from "utility/attributeValueLogic";

/**
 * Thin presentational control for a single product-instance attribute value.
 *
 * Renders the input appropriate to the attribute's declared type but ALWAYS
 * reports its value back through `onChange` as a string — the API's wire format
 * (see attributeValueLogic). This gives type-appropriate UX and soft
 * validation (e.g. a boolean can't be set to free text) while keeping transport
 * unchanged. Unknown / absent type falls back to a plain text input.
 *
 * @param {{
 *   type?: string,                 // raw attribute_definition_type
 *   value?: string,                // current value as a string
 *   name?: string,                 // attribute name (for placeholder)
 *   disabled?: boolean,
 *   onChange: (stringValue: string) => void,
 * }} props
 */
export default function AttributeValueField({
  type,
  value,
  name,
  disabled,
  onChange,
}) {
  const attrType = normalizeAttrType(type);
  const strValue = value === null || value === undefined ? "" : String(value);
  const placeholder = `Enter ${name || "value"}`;

  if (attrType === ATTR_TYPE.BOOLEAN) {
    // A Yes/No Select (not a Switch) so its height/width matches the other
    // field types. Yes/No write the "true"/"false" strings; null AND blank
    // (incl. whitespace) both read as nothing selected — never a false "No".
    const boolValue = isBlankValue(value)
      ? undefined
      : isTruthyBoolString(value)
      ? BOOL_TRUE
      : BOOL_FALSE;
    return (
      <Select
        className="standard-dropdown"
        style={{ width: "100%" }}
        value={boolValue}
        disabled={disabled}
        allowClear
        placeholder={`Select ${name || "value"}`}
        onChange={(v) => onChange(v ?? "")}
        options={[
          { value: BOOL_TRUE, label: "Yes" },
          { value: BOOL_FALSE, label: "No" },
        ]}
      />
    );
  }

  if (attrType === ATTR_TYPE.NUMBER) {
    return (
      <InputNumber
        className="standard-input"
        style={{ width: "100%" }}
        value={strValue === "" ? undefined : Number(strValue)}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(num) => onChange(numberToWire(num))}
      />
    );
  }

  if (attrType === ATTR_TYPE.DATE_TIME) {
    const parsed = strValue ? moment(stripZoneRegion(strValue)) : null;
    const dateValue = parsed && parsed.isValid() ? parsed : null;
    return (
      <DatePicker
        className="standard-input"
        style={{ width: "100%" }}
        showTime
        value={dateValue}
        disabled={disabled}
        // Send an ISO-8601 UTC string (parseable as a ZonedDateTime server-side).
        // Exact expected format is pending gandalf confirmation — see handoff.
        onChange={(m) => onChange(dateToWire(m))}
      />
    );
  }

  // TEXT / unknown / absent -> plain text input (the pre-existing behavior).
  return (
    <Input
      className="standard-input"
      value={strValue}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
